import Head from 'next/head'

import { toProductDataItem } from 'layout/navigation/search/utils'
import { serverSideConfigs, t } from 'localization'
import { SearchFiltersRefinement, SortOption } from 'search'
import { getConsumerSearchServerQueries } from 'server/utils/consumerSearchUtils'
import Grid, { GridConfig } from 'shared/components/Grid'
import { ProductSearchResponse } from 'shared/types/ConsumerSearch'
import { SearchState } from 'shared/types/Search'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'
import { transformAggregationsToStructuredData } from 'shared/utils/search/cs-utils'
import { urlToSearchState } from 'shared/utils/search/search-url-utils'

const TP = 'pages.trending'

type TrendingPageProps = {
  consumerSearchResult: ProductSearchResponse
  consumerSearchTrendingResults: ProductSearchResponse
  queryParamSearchState: SearchState
}

export const TrendingPage = ({
  consumerSearchResult,
  consumerSearchTrendingResults,
  queryParamSearchState,
}: TrendingPageProps) => {
  const hiddenFilters: Set<string> = new Set()
  // currently presentation sizes are only used for apparel
  hiddenFilters.add(SearchFiltersRefinement.PresentationSizes)
  const config: GridConfig = {
    context: { hiddenFilters, index: SortOption.Trending, hasNewProductOnly: false },
    regex: new RegExp(`/trending`),
    indexPath: '/trending',
  }

  return (
    <>
      <Head>
        <title>{t(`${TP}.pageTitle`, 'Trending | Flight Club')}</title>
      </Head>
      <Grid
        config={config}
        consumerSearchResult={consumerSearchResult}
        consumerSearchTrendingResults={consumerSearchTrendingResults}
        consumerSearchState={queryParamSearchState}
        title={t(`${TP}.trending`, 'Trending')}
      />
    </>
  )
}

export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { req, res, locale, query: qsSNext } = context
  const queryParamSearchState: SearchState = qsSNext ? urlToSearchState(qsSNext) : {}

  const [consumerSearchResults, allFiltersSearchResults, trendingSearchResults] =
    await Promise.allSettled(
      getConsumerSearchServerQueries({ consumerSearchState: queryParamSearchState, req }),
    )

  const { error: consumerSearchError, data: consumerSearchData } =
    consumerSearchResults.status === 'fulfilled' ? consumerSearchResults.value : {}

  const { data: allFiltersData } =
    allFiltersSearchResults.status === 'fulfilled' ? allFiltersSearchResults.value : {}

  const { error: trendingError, data: trendingData } =
    trendingSearchResults.status === 'fulfilled' ? trendingSearchResults.value : {}

  const consumerSearchResult = {
    aggregation: {
      filters: transformAggregationsToStructuredData(
        consumerSearchData?.aggregationValuesList || [],
      ),
    },
    allFilters: transformAggregationsToStructuredData(allFiltersData?.aggregationValuesList || []),
    data: toProductDataItem(consumerSearchData?.productsList || []),
    error: consumerSearchError || null,
    total: consumerSearchData?.totalResults || 0,
  }

  const consumerSearchTrendingResults = {
    data: toProductDataItem(trendingData?.productsList || []),
    error: trendingError || null,
    total: trendingData?.totalResults || 0,
  }

  return {
    props: {
      ...(await getServerPageProps(context)),
      ...(await serverSideConfigs(res, locale)),
      ...JSON.parse(
        JSON.stringify({
          queryParamSearchState,
          consumerSearchResult,
          consumerSearchTrendingResults,
        }),
      ),
    },
  }
}

export default TrendingPage
